"use client";

import { useSession } from "@saas/auth/hooks/use-session";
import { ImageUploadTabs } from "@shared/components/ImageUploadTabs";
import { useRouter } from "@shared/hooks/router";
import { Button } from "@ui/components/button";
import { Card, CardContent } from "@ui/components/card";
import {} from "@ui/components/dialog";
import {
	Box,
	CheckCircle,
	Copy,
	FileText,
	Globe,
	HistoryIcon,
	ImageIcon,
	InfinityIcon,
	X,
	Zap,
	ZoomIn,
} from "lucide-react";
import { useTranslations } from "next-intl";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { useImageToPrompt } from "../hooks/use-image-to-prompt";
import type { AIModel } from "../types";
import { LanguageSelector } from "./LanguageSelector";

export function ImagePromptConverter() {
	const t = useTranslations();

	const [zoomImage, setZoomImage] = useState<string | null>(null);
	const router = useRouter();
	const generatedPromptRef = useRef<HTMLDivElement>(null);
	const { user } = useSession();
	const {
		imagePreviewUrl,
		isDefaultImage,
		selectedModel,
		promptLanguage,
		generatedPrompt,
		usesRemaining,
		hasUnlimitedCredits,
		isPending,
		handleImageSelected,
		handleGeneratePrompt,
		setSelectedModel,
		setPromptLanguage,
	} = useImageToPrompt();

	// 当生成的提示变化时，自动滚动到生成的内容
	useEffect(() => {
		if (generatedPrompt && generatedPromptRef.current) {
			// 使用smooth滚动到生成的内容区域
			generatedPromptRef.current.scrollIntoView({
				behavior: "smooth",
				block: "start",
			});
		}
	}, [generatedPrompt]);

	// 当图片被选中时触发事件
	useEffect(() => {
		if (imagePreviewUrl) {
			// 触发图片上传完成事件，通知FloatingCTA组件
			const event = new CustomEvent("imageUploaded");
			window.dispatchEvent(event);
		}
	}, [imagePreviewUrl]);

	const handleCopyPrompt = () => {
		if (generatedPrompt) {
			navigator.clipboard.writeText(generatedPrompt);
			toast.success(
				t("imageToPrompt.components.promptConverter.promptCopied"),
			);
		}
	};

	const handleNavigateToImageGenerator = () => {
		if (generatedPrompt) {
			// 将生成的prompt存储到localStorage
			localStorage.setItem("transferredPrompt", generatedPrompt);
			// 跳转到AI图像生成器页面
			router.push("/ai-image-generator");
		}
	};

	const handleGetMoreCredits = () => {
		router.push("/app/subscription");
	};

	const handleButtonClick = () => {
		// Check if user is logged in
		if (!user) {
			// 触发展示登录对话框的事件，而不是重定向
			if (typeof window !== "undefined") {
				// 确保使用完整的路径（包含任何URL参数）
				const redirectPath =
					window.location.pathname + window.location.search;
				console.log("Triggering login with redirect to:", redirectPath);
				window.dispatchEvent(
					new CustomEvent("show-login", {
						detail: { redirectTo: redirectPath },
					}),
				);
			}
			return;
		}

		// User is logged in, proceed with prompt generation
		handleGeneratePrompt();
	};

	return (
		<>
			<div>
				<div className="mb-4">
					<div className="flex items-center justify-between mb-6">
						<div>{/* Title and description removed */}</div>
						<a
							href="/batch-image-to-prompt"
							className="inline-flex items-center gap-1 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 transition-colors px-3 py-1.5 rounded-full text-sm font-medium"
						>
							<FileText className="h-3.5 w-3.5" />
							{t(
								"imageToPrompt.components.promptConverter.batchConvert",
							)}
						</a>
					</div>

					<div className="flex flex-col md:flex-row gap-8">
						<div className="md:w-1/2">
							<Card className="h-full border border-indigo-200 hover:border-indigo-300 transition-colors shadow-sm">
								<CardContent className="p-4">
									<ImageUploadTabs
										onImageSelected={handleImageSelected}
									/>
								</CardContent>
							</Card>
						</div>

						<div className="md:w-1/2 image-preview-container">
							<div className="rounded-lg border-2 border-solid border-slate-200 flex items-center justify-center h-[422px] max-h-[422px] bg-white overflow-hidden">
								{imagePreviewUrl ? (
									<div className="relative w-full h-full flex items-center justify-center group overflow-hidden">
										<img
											src={imagePreviewUrl}
											alt="Preview"
											className="max-w-full max-h-full object-contain"
										/>
										<div className="absolute right-3 top-3 flex gap-2 opacity-0 transition-opacity duration-200 group-hover:opacity-100">
											<Button
												variant="ghost"
												size="icon"
												onClick={() =>
													handleImageSelected(
														null,
														"",
													)
												}
												className="bg-background/80 hover:bg-background shadow-sm backdrop-blur-sm"
												aria-label={t(
													"shared.actions.remove",
												)}
												type="button"
											>
												<X className="h-4 w-4" />
											</Button>
											<Button
												variant="ghost"
												size="icon"
												onClick={() =>
													setZoomImage(
														imagePreviewUrl,
													)
												}
												className="bg-background/80 hover:bg-background shadow-sm backdrop-blur-sm"
												title={t("shared.actions.zoom")}
											>
												<ZoomIn className="h-4 w-4" />
											</Button>
										</div>
									</div>
								) : (
									<div className="text-center">
										<ImageIcon className="h-8 w-8 mx-auto text-gray-400 mb-2" />
										<p className="text-gray-400 font-medium mb-2">
											{t(
												"imageToPrompt.components.uploadTabs.imageWillAppear",
											)}
										</p>
									</div>
								)}
							</div>
						</div>
					</div>

					<div className="mt-8 border rounded-xl p-5 bg-background/50 shadow-sm">
						<div className="space-y-4">
							<div className="flex items-center justify-between">
								<h2 className="text-sm font-medium">
									{t(
										"imageToPrompt.components.promptConverter.promptTarget",
									)}
								</h2>
							</div>

							<div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
								{[
									{
										id: "general" as AIModel,
										icon: Globe,
										label: t(
											"imageToPrompt.components.modelLabels.general",
										),
									},
									{
										id: "flux" as AIModel,
										icon: Zap,
										label: t(
											"imageToPrompt.components.modelLabels.flux",
										),
									},
									{
										id: "stable-diffusion" as AIModel,
										icon: Box,
										label: t(
											"imageToPrompt.components.modelLabels.stableDiffusion",
										),
									},
									{
										id: "midjourney" as AIModel,
										icon: ImageIcon,
										label: t(
											"imageToPrompt.components.modelLabels.midjourney",
										),
									},
								].map((model) => (
									<button
										key={model.id}
										type="button"
										onClick={() =>
											setSelectedModel(model.id)
										}
										className={`relative flex items-center justify-center gap-1 p-3 rounded-lg transition-colors bg-gray-50 hover:bg-gray-100 ${
											selectedModel === model.id
												? "border-2 border-[#6366F1]"
												: "border border-gray-200"
										}`}
									>
										{selectedModel === model.id && (
											<div className="absolute top-2 right-2 text-[#6366F1]">
												<CheckCircle
													size={16}
													aria-label="Selected"
												/>
											</div>
										)}
										<model.icon
											className={
												selectedModel === model.id
													? "text-[#6366F1]"
													: "text-gray-500"
											}
											size={20}
											aria-label={`${model.label} icon`}
											aria-hidden="true"
										/>
										<span className="text-xs sm:text-sm font-medium truncate">
											{model.label}
										</span>
									</button>
								))}
							</div>

							<div className="flex items-center justify-between mt-6">
								<h2 className="text-sm font-medium">
									{t(
										"imageToPrompt.components.promptConverter.promptLanguage",
									)}
								</h2>
							</div>

							<div className="grid grid-cols-1 gap-4 md:grid-cols-12 md:items-center">
								<div className="md:col-span-3">
									<LanguageSelector
										value={promptLanguage}
										onChange={setPromptLanguage}
									/>
								</div>

								<div className="md:col-span-9">
									<Button
										className={`w-full h-12 ${
											!user ||
											hasUnlimitedCredits() ||
											Math.max(0, usesRemaining) > 0
												? "bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700"
												: "bg-gray-300 hover:bg-gray-400 text-gray-700"
										} shadow-md transition-all duration-300`}
										variant={
											isPending ? "outline" : "primary"
										}
										onClick={handleButtonClick}
										disabled={
											!imagePreviewUrl ||
											isDefaultImage ||
											isPending ||
											(!!user &&
												!hasUnlimitedCredits() &&
												Math.max(0, usesRemaining) <= 0)
										}
									>
										{isPending ? (
											<>
												<div className="h-4 w-4 mr-2 animate-spin rounded-full border-2 border-current border-t-transparent" />
												{t(
													"imageToPrompt.components.promptConverter.generating",
												)}
											</>
										) : (
											t(
												"imageToPrompt.components.promptConverter.generatePrompt",
											)
										)}
									</Button>
								</div>
							</div>

							{user && (
								<div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 mt-4">
									<div className="flex items-center gap-2 flex-wrap">
										<span className="text-sm px-3 py-1 bg-indigo-50 text-indigo-700 rounded-full font-medium">
											{t(
												"imageToPrompt.components.promptConverter.credits",
											)}{" "}
											{typeof usesRemaining ===
												"string" &&
											usesRemaining === "infinity" ? (
												<>
													<InfinityIcon className="inline-block h-4 w-4 align-middle mx-1" />
												</>
											) : (
												<span>
													{Math.max(0, usesRemaining)}
												</span>
											)}{" "}
											💎
										</span>
										<Button
											onClick={handleGetMoreCredits}
											variant="ghost"
											size="sm"
											className="text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50"
										>
											{t(
												"imageToPrompt.components.promptConverter.getMore",
											)}
										</Button>
									</div>
									<Button
										variant="ghost"
										size="sm"
										className="gap-2 text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50"
										onClick={() => {
											// Open history dialog in parent component via a prop
											if (typeof window !== "undefined") {
												window.dispatchEvent(
													new CustomEvent(
														"openPromptHistory",
													),
												);
											}
										}}
									>
										<HistoryIcon className="h-4 w-4" />
										{t(
											"imageToPrompt.components.promptConverter.viewHistory",
										)}
									</Button>
								</div>
							)}
						</div>
					</div>
				</div>

				{generatedPrompt && (
					<div className="mt-6" ref={generatedPromptRef}>
						<div className="flex justify-between items-center mb-2">
							<h3 className="text-lg font-medium">
								{t(
									"imageToPrompt.components.promptConverter.generatedPrompt",
								)}
							</h3>
						</div>

						<div className="bg-muted/10 p-4 rounded-lg border relative">
							<Button
								variant="ghost"
								size="icon"
								onClick={handleCopyPrompt}
								className="absolute top-2 right-2 h-8 w-8 hover:bg-background/80"
							>
								<Copy className="h-4 w-4" />
								<span className="sr-only">
									{t(
										"imageToPrompt.components.promptConverter.copyPrompt",
									)}
								</span>
							</Button>
							<div className="whitespace-pre-wrap pr-10 max-h-[200px] overflow-y-auto text-sm sm:text-base break-words">
								{generatedPrompt}
							</div>
						</div>

						<div className="mt-4">
							<Button
								onClick={handleNavigateToImageGenerator}
								className="w-full h-12 bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 shadow-md transition-all duration-300 flex items-center justify-center gap-2"
							>
								<ImageIcon className="h-4 w-4" />
								{t(
									"imageToPrompt.components.promptConverter.generateImage",
								)}
							</Button>
						</div>
					</div>
				)}
			</div>

			{/* Zoom Image Modal */}
			{zoomImage && (
				<dialog
					className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 m-0 w-full h-full backdrop-blur-sm"
					onClick={() => setZoomImage(null)}
					onKeyUp={(e) => {
						if (e.key === "Escape") setZoomImage(null);
					}}
					open
				>
					<div className="relative max-w-5xl max-h-[90vh] w-full h-full">
						<img
							src={zoomImage}
							alt="Full view of uploaded content"
							className="w-full h-full object-contain"
						/>
						<button
							className="absolute top-2 right-2 text-white bg-black/50 rounded-full p-2 hover:bg-black/70 transition-colors"
							onClick={() => setZoomImage(null)}
							type="button"
							aria-label={t("shared.actions.close")}
						>
							<X className="h-6 w-6" />
						</button>
					</div>
				</dialog>
			)}
		</>
	);
}
